import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import dynamic from 'next/dynamic';
import { useTheme } from 'styled-components';
import * as S from './RestoredAdModalSheet.styles';
import { promotions } from '@/components/Ad/Promotions/Promotions.constants';
import { getLessPrice } from '@/components/Ad/Promotions/Promotions.helpers';
import { Promotion } from '@/components/Ad/Promotions/Promotion';
import { Button } from '@/components/UI/Button/Button';
import { useAdDetails } from '@/hooks/useAdDetails';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { AdStatus } from '@/types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useServerDataContext } from '@/contexts';
import { AdKind } from '@/api/types/common.types';
import { ModalSheet } from '@/components/UI/ModalSheet';

interface RestoredAdModalSheetProps {
  isOpen: boolean;
  toggle: (value?: boolean) => void;
}

const PromotionFlow = dynamic(
  () =>
    import('@/components/Ad/Promotions/PromotionFlow').then(
      mod => mod.PromotionFlow
    ),
  { ssr: false }
);

export const RestoredAdModalSheet = ({
  isOpen,
  toggle,
}: RestoredAdModalSheetProps) => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const theme = useTheme();
  const adData = adDetails ?? initialAdDetails;
  const [isDrawerOpen, toggleDrawer] = useToggleOpenState();
  const [selectedType, setSelectedType] = useState<AdKind | null>(null);
  const { isWebView } = useBridgeState();

  function handleSelectPromotion(type: AdKind) {
    setSelectedType(type);
    toggleDrawer();
  }

  return (
    <ModalSheet
      isInPortal
      dataTestId={AdPageTestIds.statusBanner.restoredAdWindow}
      isOpen={isOpen}
      onClose={() => toggle(false)}
      title={t('pages.ad.ad_is_restored')}
      background={theme.palette.secondary.background}
    >
      <S.Container>
        <S.Box>
          <S.RestoredAdDescription>
            <Typography.Body>
              {t('pages.ad.ad_is_restored_subtitle')}
            </Typography.Body>
          </S.RestoredAdDescription>

          <Typography.Caption $variant="2">
            {t('pages.ad.ad_number')}
          </Typography.Caption>

          <S.AdNumber>{adData?.legacyResourceId}</S.AdNumber>
        </S.Box>

        <S.Box>
          <S.PromotionTitle>
            {t('pages.ad.ad_is_restored_promotion')}
          </S.PromotionTitle>
          {adData && (
            <S.PromotionsContainer>
              {promotions(
                t,
                getLessPrice(adData.bumpServices[0]),
                getLessPrice(adData.vipServices[0]),
                getLessPrice(adData.featuredServices[0]),
                adData.status === AdStatus.Published
              ).map(promotion => (
                <Promotion
                  key={promotion.type}
                  promotion={promotion}
                  onSelect={handleSelectPromotion}
                />
              ))}
            </S.PromotionsContainer>
          )}

          <PromotionFlow
            isInPortal
            isOpen={isDrawerOpen}
            toggle={toggleDrawer}
            onSuccessPayment={toggle}
            type={selectedType}
            hideSuccessModal={!isWebView}
          />
        </S.Box>
      </S.Container>

      <S.ActionsContainer>
        <Button
          appearance="secondaryLight"
          isFullWidth
          borderRadius={14}
          onClick={() => toggle(false)}
          data-testid={AdPageTestIds.statusBanner.restoredAdWindowBtn}
        >
          {t('pages.ad.ad_is_restored_button')}
        </Button>
      </S.ActionsContainer>
    </ModalSheet>
  );
};
